// Crossfire: where the experts you follow disagree. Three tabs, New /
// Undecided / Decided. Practice never picks a winner: not in copy, not by
// ordering, not by emphasis. Runs the full page width and returns no rail.
// Search runs client-side, because every question already ships: follows
// scope is the `mine` flag, Everyone is the whole list (spec section 5).
import { useEffect, useState } from "react";
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { roleLine, useExpert } from "../shell/fragments.js";
import { scopeTabs, SearchBox } from "../shell/SurfaceSearch.js";
import { paragraphs } from "../../shared/prose.js";
import { XF_FALLBACK } from "../shell/placeholderArt.js";
import { CrossfireHero } from "../shell/CrossfireHero.js";
import { sheetScrollNow, useStore, type AppStore } from "../state/store.js";
import type { CrossfireQuestionView } from "../state/types.js";
import { artImg } from "../assets.js";

/* Read off the stylesheet rather than guessed (3 Aug 2026). The tile grid is
   `repeat(auto-fill, minmax(260px, 1fr))`, which put it between 319 and 359
   across every viewport tried. A lane picture is the 220px column of
   `.xlane`, and that layout stacks to full width at 680px, not at some round
   number nearby: a breakpoint that is wrong by a hundred pixels makes the
   browser fetch the 1200 original for a 220px slot, which is the exact waste
   the ladder exists to stop. */
const XF_TILE_SIZES = "(max-width: 520px) 100vw, 360px";
const XLANE_SIZES = "(max-width: 680px) 100vw, 220px";

function LoadOnce() {
  const crossfire = useStore((s) => s.crossfire);
  const loadCrossfire = useStore((s) => s.loadCrossfire);
  useEffect(() => {
    if (!crossfire) void loadCrossfire();
  }, []);
  return null;
}

// "Says ..." makes plain whose voice the position is: the expert's, never
// ours (28 Jul 2026). The first letter drops to lowercase unless it looks
// like a proper noun or an acronym.
export function saysText(position: string): string {
  if (/^says\b/i.test(position)) return position;
  const rest =
    position.length > 1 && position[1] === position[1]?.toLowerCase()
      ? (position[0]?.toLowerCase() ?? "") + position.slice(1)
      : position;
  return `Says ${rest}`;
}

// A prose field, in the paragraphs the corpus wrote. One paragraph is the
// common case and renders exactly as it did before.
function Prose({ text }: { text: string }) {
  return (
    <>
      {paragraphs(text).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

/* One position, built the way the public page builds one (2 Aug 2026, Rob:
   "the crossfire modal should model the landing page"). It had been a card in
   a two-column grid with a coloured left edge, which reads as an interface
   listing options; the landing page reads as an article, and an article is
   what a disagreement is.

   So: the person as a byline with their face and their role, the position set
   large in the serif, what it rests on under a mono label, the source, and a
   picture beside it that alternates side down the page. The lane colour goes
   with the card, because the position no longer needs a coloured edge to say
   it is somebody's: the byline above it already does, in words.

   What the landing page does not have and this keeps: the two controls. Read
   the full position, and Go with them. */
function Lane({ q, laneId }: { q: CrossfireQuestionView; laneId: string }) {
  const s = useStore();
  const lane = q.lanes.find((l) => l.id === laneId);
  const e = useExpert(lane?.expertId ?? lane?.expertKey ?? "");
  if (!lane) return null;
  const cur = [...q.history].reverse().find((h) => h.pick !== "open") ?? null;
  const isCur = q.status === "decided" && cur?.pick === (lane.expertId ?? lane.expertKey);
  return (
    <article className={`xlane${lane.art ? "" : " noart"}`}>
      <div className="xlane-body">
        {/* The face and the name open the person as a modal over the
            question (2 Aug 2026, Rob), and close back to it at the scroll they
            were left at, the same as a practice. It used to navigate to the
            expert screen, which closed the question outright: a reader
            checking who somebody is, mid-argument, lost the argument to find
            out. The peek is the right sheet for it because it carries the
            follow control and the per-expert topic mutes, so the check and
            the decision that usually follows it both happen without leaving.
            Their full profile is still one press further in. */}
        <button
          className="xlane-who"
          onClick={(ev) => {
            ev.stopPropagation();
            if (lane.expertId) {
              s.openSheet(
                { kind: "expert-peek", id: lane.expertId },
                { sheet: { kind: "crossfire", questionId: q.id }, scroll: sheetScrollNow() },
              );
            }
          }}
        >
          <Avatar e={e} size="m" />
          <span>
            <b>{e.name}</b>
            {roleLine(e) && <span>{roleLine(e)}</span>}
          </span>
        </button>
        <p className="xlane-says">{saysText(lane.position)}</p>
        {lane.basis && (
          <p className="xlane-rests">
            <span className="k">What it rests on</span>
            {lane.basis}
          </p>
        )}
        <div className="xlane-acts">
          <button
            className="txt xlane-more"
            onClick={(ev) => {
              ev.stopPropagation();
              s.openSheet(
              { kind: "lane", questionId: q.id, laneId: lane.id },
              { sheet: { kind: "crossfire", questionId: q.id }, scroll: sheetScrollNow() },
            );
            }}
          >
            Read the full position ›
          </button>
          {isCur ? (
            <span className="btn accent" style={{ cursor: "default" }}>
              Your call ✓
            </span>
          ) : lane.expertId ? (
            <button className="btn" onClick={() => void s.pickStance(q.id, lane.expertId ?? "")}>
              Go with {e.short ?? e.name}
            </button>
          ) : null}
        </div>
      </div>
      {lane.art && (
        <div className="xlane-art">
          <img alt="" loading="lazy" decoding="async" sizes={XLANE_SIZES} {...artImg(lane.art)} />
        </div>
      )}
    </article>
  );
}

/* The body of one disagreement, and since 2 Aug 2026 the contents of the
   crossfire modal rather than a block on a page of them. It kept every part
   and every order it had as QuestionBox: lanes, your standing, your history,
   the three prose blocks, the practices drawn from it. What it lost is the
   panel around it, which the sheet now provides, and the "the one you came
   from" banner, which meant something only when it was one box among many. */
export function QuestionDetail({ q, outside }: { q: CrossfireQuestionView; outside?: boolean }) {
  const s = useStore();
  const cur = [...q.history].reverse().find((h) => h.pick !== "open") ?? null;
  const curExpert = cur ? s.appState?.experts[cur.pick] : undefined;

  let status: React.ReactNode;
  if (q.status === "decided" && cur) {
    status = (
      <>
        <p style={{ marginTop: 14 }}>
          <b>You’re going with {curExpert?.short ?? curExpert?.name ?? cur.pick}</b>, for now · since {cur.date}
        </p>
        <div className="btnrow" style={{ marginTop: 10 }}>
          {q.lanes
            .filter((l) => l.expertId && l.expertId !== cur.pick)
            .map((l) => {
              const other = s.appState?.experts[l.expertId ?? ""];
              return (
                <button key={l.id} className="btn quiet" onClick={() => void s.pickStance(q.id, l.expertId ?? "")}>
                  Switch to {other?.short ?? other?.name ?? l.expertKey}
                </button>
              );
            })}
          <button className="btn quiet" onClick={() => void s.pickStance(q.id, "open")}>
            Undecide
          </button>
        </div>
      </>
    );
  } else if (q.status === "undecided") {
    const last = q.history.at(-1);
    status = (
      <p style={{ marginTop: 14 }}>
        <b>Still deciding, and that’s recorded</b>
        {last ? ` · since ${last.date}` : ""}. We’ll surface new evidence as it lands, without nagging.
      </p>
    );
  } else {
    status = (
      <p className="xfundecided">
        <button className="btn" onClick={() => void s.pickStance(q.id, "open")}>
          Still deciding
        </button>{" "}
        Pick a side to follow, or record that you’re still deciding. You can always change your mind later.
      </p>
    );
  }

  /* The order is the public page's (2 Aug 2026, Rob), which reads as an
     article rather than as a form: the positions first under a heading that
     says what they are, then the three passages that frame them. The one
     thing inserted into that order is the decision, which sits directly under
     the positions because it is the answer to them, and because a reader who
     has made up their mind should not have to scroll past a thousand words of
     context to say so. */
  return (
    <div className="xfdetail">
      {outside && (
        <div className="notice info" style={{ marginBottom: 14 }}>
          <span className="ni">◎</span>
          <span>
            No one you follow is in this disagreement. It’s open to look at either way; following
            someone in it adds it to yours.
          </span>
        </div>
      )}
      <h2 className="xfh">What the experts say</h2>
      <div className="xlanes">
        {q.lanes.map((l) => (
          <Lane key={l.id} q={q} laneId={l.id} />
        ))}
      </div>
      {status}
      {(q.populationCaveat || q.agreement || q.wouldChange) && (
        <>
          {q.populationCaveat && (
            <>
              <h2 className="xfh">Overview</h2>
              <div className="xfnote">
                <Prose text={q.populationCaveat} />
              </div>
            </>
          )}
          {q.agreement && (
            <>
              {/* "Where they agree" (2 Aug 2026, Rob), the same three headings
                  the public page uses and in the same order, so a reader who
                  arrives from a shared link finds the question laid out the
                  way they just read it. It also retires the both/every
                  conditional this label used to need: a question can reach
                  seven positions, and "both sides" over seven people is wrong
                  on its face. A label that never counts cannot miscount. */}
              <h2 className="xfh">Where they agree</h2>
              <div className="xfnote">
                <Prose text={q.agreement} />
              </div>
            </>
          )}
          {q.wouldChange && (
            <>
              <h2 className="xfh">What would settle it</h2>
              <div className="xfnote">
                <Prose text={q.wouldChange} />
              </div>
            </>
          )}
        </>
      )}
      {q.cardIds.length > 0 && <DrawnPractices cardIds={q.cardIds} questionId={q.id} />}
      {q.recheckDate && <p className="xfreask">Re-ask {q.recheckDate}</p>}
      {q.history.length > 0 && (
        <div className="hist">
          <span className="u">Your history</span>
          {q.history.map((h, i) => (
            <div className="histrow" key={i}>
              <span>
                {h.label ?? h.pick}
                {h.note ? ` · ${h.note}` : ""}
              </span>
              <span>{h.date}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Three rows, then a quiet reveal: some questions implicate a dozen
// practices and the list would drown the argument above it.
function DrawnPractices({ cardIds, questionId }: { cardIds: string[]; questionId: string }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? cardIds : cardIds.slice(0, 3);
  return (
    <div style={{ marginTop: 14 }}>
      <span className="u">Practices from this</span>
      {shown.map((cid) => (
        <PracticeRow key={cid} cardId={cid} questionId={questionId} />
      ))}
      {!showAll && cardIds.length > 3 && (
        <button className="txt" style={{ padding: "8px 8px 0" }} onClick={() => setShowAll(true)}>
          Show {cardIds.length - 3} more ›
        </button>
      )}
    </div>
  );
}

// Full-width rows, not chips: the do-lines are whole sentences and need a
// line of their own to read.
function PracticeRow({ cardId, questionId }: { cardId: string; questionId: string }) {
  const s = useStore();
  const card = s.appState?.cards[cardId] ?? s.cardDetails[cardId]?.card;
  useEffect(() => {
    if (!card) void s.loadCardDetail(cardId);
  }, [cardId, Boolean(card)]);
  if (!card) return null;
  const from = card.expertId ? (s.appState?.experts[card.expertId]?.name ?? card.expertId) : "you";
  return (
    <button
      className="lrow"
      style={{ padding: "10px 8px" }}
      /* Back to the question, at the scroll it was left at (2 Aug 2026,
         Rob): a question can draw a dozen practices, and without this each
         one costs a close, a re-open and a scroll back down to find the next.
         The point of the list is reading several. */
      onClick={() =>
        s.openSheet(
          { kind: "practice", cardId },
          { sheet: { kind: "crossfire", questionId }, scroll: sheetScrollNow() },
        )
      }
    >
      <span className="grow">
        <strong style={{ fontSize: 14.5 }}>{card.do}</strong>
        <small style={{ marginTop: 2 }}>From {from}</small>
      </span>
      <span className="chev">›</span>
    </button>
  );
}

/* A question in the list (2 Aug 2026, Rob). The picture is the change: this
   screen used to be a column of prose with nothing to look at, and the
   illustrations exist now, so the list can work the way Discover's does.

   The whole tile opens the modal. Nothing on it is dressed as a second
   control, for the reason the Discover hero states: a button on a photograph
   reads as an advertisement. The faces are the argument in miniature, so the
   reader can see who is in it before deciding to open it.

   No art of its own falls back to the topic stand-in, so the grid never mixes
   illustrated and bare tiles. A question has no topic, so the fallback comes
   from the practices it draws, and failing that the default. */
function QuestionCard({ q }: { q: CrossfireQuestionView }) {
  const s = useStore();
  const experts = useStore((x) => x.experts) ?? [];
  const named = q.lanes.filter((l) => l.expertId);
  const faces = named
    .map((l) => experts.find((e) => e.id === l.expertId))
    .filter((e): e is NonNullable<typeof e> => Boolean(e));
  const cur = [...q.history].reverse().find((h) => h.pick !== "open") ?? null;
  const curExpert = cur ? s.appState?.experts[cur.pick] : undefined;
  const standing =
    q.status === "decided" && curExpert
      ? `Going with ${curExpert.short ?? curExpert.name}`
      : q.status === "undecided"
        ? "Still deciding"
        : null;
  return (
    <button className="xfcard" onClick={() => s.openSheet({ kind: "crossfire", questionId: q.id })}>
      {/* An <img>, not a background (3 Aug 2026). A background is fetched the
          moment the element is laid out, whether or not it is anywhere near
          the viewport and whether or not the screen has 80 of them, and this
          grid does: entering Crossfire pulled 82 pictures and 6,277KB
          measured. loading="lazy" is the whole fix, and the srcset is the
          second one, since the tile renders 319 to 359 wide at every viewport
          and was taking a 1200-wide original at all of them. */}
      <img
        className="xfcard-art"
        alt=""
        loading="lazy"
        decoding="async"
        sizes={XF_TILE_SIZES}
        {...artImg(q.image ?? XF_FALLBACK)}
      />
      <span className="xfcard-body">
        <span className="xfcard-q">{q.question}</span>
        <span className="xfcard-who">
          {/* Faces, then the count. Six is where the row stops being people
              and starts being a texture. */}
          <span className="xfcard-faces">
            {faces.slice(0, 6).map((e) => (
              <Avatar key={e.id} e={e} size="s" />
            ))}
          </span>
          <span className="xfcard-n">
            {named.length} position{named.length === 1 ? "" : "s"}
          </span>
        </span>
        {/* Your standing, when you have one. Never a winner: this says what
            you recorded, not what is true. */}
        {standing && <span className="xfcard-stand">{standing}</span>}
        {/* A search in Everyone scope reaches past the people you follow, and
            the tile has to say so where the old compact row did (2 Aug 2026).
            Quiet, not the accent the standing takes: it is a fact about the
            result, not something the reader decided. */}
        {!q.mine && <span className="xfcard-outside">outside your follows</span>}
      </span>
    </button>
  );
}

/* A deep link opens the modal. An effect and not a call during render,
   because opening a sheet is a store write and the descriptor is built inside
   a render pass. Keyed on the id so arriving at a second question from a
   different practice opens that one, and so closing the sheet does not
   immediately reopen it: the id in the route is what changed, not the sheet.
   Rendered as an element rather than a hook in the screen function for the
   same reason LoadOnce is: these screens are plain functions, not components. */
function OpenFocused({ id }: { id: string | null }) {
  const openSheet = useStore((s) => s.openSheet);
  const nav = useStore((s) => s.nav);
  useEffect(() => {
    if (!id) return;
    openSheet({ kind: "crossfire", questionId: id });
    // The route goes back to the bare screen once the modal has it, so the
    // reader who closes the sheet is left on the list rather than on a URL
    // that would reopen it the moment anything re-renders.
    nav("crossfire");
  }, [id]);
  return null;
}

export function crossfire(state: AppStore, focusId: string | null): Descriptor {
  const list = state.crossfire;

  // The toolbar row: search above the filter bar, same shape on every
  // surface (29 Jul 2026). No source select here: a disagreement has no
  // single source, that is the point of it.
  const bar = (
    <div className="searchbar">
      <SearchBox
        value={state.xfQ}
        onChange={(q) => state.setXfQ(q)}
        placeholder="Search crossfire…"
        label="Search crossfire"
      />
    </div>
  );

  // A live query takes over the list, client-side: every question is
  // already in the payload.
  if (state.xfQ.trim() !== "") {
    const needle = state.xfQ.trim().toLowerCase();
    const pool = (list ?? []).filter((q) => state.xfScope === "all" || q.mine);
    const hits = pool.filter((q) =>
      [q.question, q.context, ...q.lanes.map((l) => l.position)]
        .some((t) => t?.toLowerCase().includes(needle)),
    );
    return {
      nav: "crossfire",
      title: "Crossfire",
      bar,
      // No source select to sit beside it: a disagreement has no single
      // source, which is the point of it.
      tabs: scopeTabs(state.xfScope, (sc) => state.setXfScope(sc)),
      main: (
        <>
          <LoadOnce />
          {list === null ? (
            <section className="panel glass">
              <p className="empty">Loading…</p>
            </section>
          ) : hits.length ? (
            /* The same tiles the list shows (2 Aug 2026, Rob: "just filtering
               the cards in the same view"). Searching used to swap the grid
               for compact rows, so finding a disagreement and browsing one
               looked like two different screens, and the row could carry
               neither the picture nor the faces. */
            <div className="xfgrid">
              {hits.map((q) => (
                <QuestionCard key={q.id} q={q} />
              ))}
            </div>
          ) : (
            <p className="empty">
              {state.xfScope === "follows"
                ? (
                    <>
                      No disagreement among the people you follow matches.{" "}
                      <button
                        className="txt"
                        style={{ textDecoration: "underline" }}
                        onClick={() => state.setXfScope("all")}
                      >
                        Search everyone.
                      </button>
                    </>
                  )
                : "Nothing matches."}
            </p>
          )}
        </>
      ),
    };
  }

  // Deep link: arriving from a practice or a profile selects the tab that
  // question is on. Choosing a tab by hand clears the argument (the trap in
  // spec section 6).
  const focused = focusId && list ? list.find((q) => q.id === focusId) : undefined;
  const tab = focused?.mine ? focused.status : state.xfTab;

  // The tabs hold your crossfire: questions with someone you follow in a
  // lane. A directly-opened question outside that set still renders,
  // nothing is gated; it sits above the tab's list instead of on a tab.
  const groups = { new: [] as CrossfireQuestionView[], undecided: [] as CrossfireQuestionView[], decided: [] as CrossfireQuestionView[] };
  for (const q of list ?? []) if (q.mine) groups[q.status].push(q);
  let shown = groups[tab as keyof typeof groups] ?? [];
  if (focused?.mine) shown = [...shown].sort((a, b) => Number(b.id === focusId) - Number(a.id === focusId));

  /* A question opened by direct link opens its modal rather than rendering
     above the list (2 Aug 2026). That includes one nobody you follow is in,
     which is why nothing is gated: the tabs are your crossfire, the link is
     still a link. */
  const openFocused = focusId && list ? list.find((q) => q.id === focusId) : undefined;

  const main = (
    <>
      <LoadOnce />
      <OpenFocused id={openFocused ? openFocused.id : null} />
      {list === null ? (
        <section className="panel glass">
          <p className="empty">Loading…</p>
        </section>
      ) : shown.length ? (
        <div className="xfgrid">
          {shown.map((q) => (
            <QuestionCard key={q.id} q={q} />
          ))}
        </div>
      ) : (
        <p className="empty">Nothing here right now.</p>
      )}
    </>
  );

  /* The hero rides above the toolbar on every tab, the way Discover's does,
     so switching tabs does not make the top of the screen jump. It teases the
     New lane specifically: those are the disagreements the reader has not
     answered, and a hero over the Decided ones would be showing them their
     own homework. Two is the floor, because one question is a still. */
  const heroQs = groups.new.filter((q) => q.image);

  return {
    nav: "crossfire",
    title: "Crossfire",
    hero: heroQs.length >= 2 ? <CrossfireHero questions={heroQs} /> : undefined,
    bar,
    tabs: {
      items: [
        ["new", "New"],
        ["undecided", "Undecided"],
        ["decided", "Decided"],
      ],
      counts: { new: groups.new.length, undecided: groups.undecided.length, decided: groups.decided.length },
      active: tab,
      onPick: (t) => state.setXfTab(t),
    },
    main,
  };
}
